'use client'

import {Children, ReactNode, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react'
import {cx} from '@local/utils/cx'
import {useResizeObserver, useWindowSize} from '@local/hooks'
import {ElementOrSelector, animate} from 'motion'
import {expoInOut} from 'eases'

export interface AutoSlideshowProps {
	amount?: number
	duration?: number
	startPlaying?: boolean
	children: ReactNode
	className?: string
	wrapperClassName?: string
	childWrapperClassName?: string
	onChange?: (index: number) => void
}

export const AutoSlideshow = forwardRef(
	(
		{
			amount = 3,
			duration = 4,
			children,
			className,
			wrapperClassName,
			childWrapperClassName,
			onChange,
		}: AutoSlideshowProps,
		parentRef,
	) => {
		const CHILDREN_COUNT = Children.count(children)
		const ITEMS_LENGHT = amount * CHILDREN_COUNT
		const OFFSET = CHILDREN_COUNT
		const UPPER_BOUND = ITEMS_LENGHT - OFFSET

		const windowSizes = useWindowSize()

		const [currentIndex, setCurrentIndex] = useState<number>(OFFSET)
		const prevIndex = useRef(-1)

		const currentIndexRef = useRef<number>(OFFSET)

		const ref = useRef()
		const setRefs = useCallback((node) => {
			ref.current = node
			if (parentRef) parentRef.current = node
		}, [])

		const containerRef = useRef<HTMLDivElement>(null)
		const wrapperRefs = useRef<Array<HTMLDivElement | null>>([])
		const wrapperWidth = useRef<number>(0)
		const [centerTranslation, setCenterTranslation] = useState<number>(0)

		useEffect(() => {
			wrapperRefs.current = wrapperRefs.current.slice(0, amount)
		}, [])

		const next = () => {
			// if index is at the start, reset the marquee translation
			if (prevIndex.current === UPPER_BOUND) {
				wrapperRefs.current.forEach(el => {
					// either one of those fails so there's a fallback
					animate(
						el as ElementOrSelector,
						{transform: `translateX(${0 - OFFSET * (wrapperWidth.current / CHILDREN_COUNT)}px)`},
						{duration: 0.001},
					)
					el.style.transform = `translateX(${0 - OFFSET * (wrapperWidth.current / CHILDREN_COUNT)}px)`
				})
			}
			// count up the index until the upper bound
			if (currentIndexRef.current < UPPER_BOUND) {
				prevIndex.current = currentIndexRef.current
				currentIndexRef.current++
				setCurrentIndex(prev => prev + 1)
				if (onChange) onChange(currentIndexRef.current)
			}

			wrapperRefs.current.forEach((el, i) => {
				animate(
					el as ElementOrSelector,
					{transform: `translate3d(${0 - currentIndexRef.current * (wrapperWidth.current / CHILDREN_COUNT)}px, 0, 0)`},
					{duration: duration - 2, easing: expoInOut, endDelay: 2},
				)
			})

			if (currentIndexRef.current === UPPER_BOUND) {
				prevIndex.current = currentIndexRef.current
				currentIndexRef.current = OFFSET
				setCurrentIndex(OFFSET)
			}
		}

		useResizeObserver(containerRef, (e) => {
			const _wrapperWidth = e.target.firstElementChild?.clientWidth ?? 0
			const _childWidth = _wrapperWidth / CHILDREN_COUNT
			wrapperWidth.current = _wrapperWidth
			setCenterTranslation((_childWidth - windowSizes.innerWidth) / 2)
		}, [CHILDREN_COUNT, windowSizes.innerWidth])

		useImperativeHandle(parentRef, () => {
			return {
				wrapperRefs: wrapperRefs.current,
				getIndex: () => currentIndex,
				next: () => next(),
			}
		}, [])

		return (
			<div
				ref={setRefs}
				className={cx(
					'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-2/3',
					'w-full flex justify-center items-center overflow-clip',
					className,
				)}>
				<div
					className={cx(
						'flex w-full flex-shrink-0 overflow-clip',
						wrapperClassName,
					)}
					ref={containerRef}>
					{Array.from({length: amount}).map((_, i) => (
						<div
							key={i}
							style={{
								gridTemplateColumns: `repeat(${CHILDREN_COUNT}, 1fr)`,
								left: -centerTranslation,
								transform: `translate3d(${0 - OFFSET * (wrapperWidth.current / CHILDREN_COUNT)}px, 0, 0)`,
							}}
							className={cx(
								'grid shrink-0 relative',
								'transition-transform transform-gpu ease-expo-inout',
								childWrapperClassName,
							)}
							ref={el => {
								wrapperRefs.current[i] = el
							}}>
							{children}
						</div>
					))}
				</div>
			</div>
		)
	},
)
